import type { EnvironmentId } from "@t3tools/contracts";

import type { MessageKey } from "~/i18n";
import type { ActivityEvent } from "./activitiesTypes";

export type ActivityDetailLinkId = "thread" | "agent" | "workflow" | "allowance" | "file";

export type ActivityDetailRouteLink = {
  readonly id: ActivityDetailLinkId;
  readonly labelKey: MessageKey;
  readonly subtitle?: string;
  readonly to: string;
  readonly params?: Record<string, string>;
  readonly search?: Record<string, string>;
};

export function activityDetailLinks(
  event: ActivityEvent,
  fallbackEnvironmentId: EnvironmentId | null,
): readonly ActivityDetailRouteLink[] {
  const { refs } = event;
  const links: ActivityDetailRouteLink[] = [];

  const environmentId = refs.environmentId ?? fallbackEnvironmentId ?? null;
  // An agent's work happens in its own chat (wakes included), so "Open agent" is where to see
  // it — the raw thread view would show the same conversation without the agent's window.
  const agentOwned =
    Boolean(refs.agentId && refs.agentId !== "main") && event.kind !== "workflow_run";
  if (refs.threadId && environmentId && !agentOwned) {
    links.push({
      id: "thread",
      // For a workflow run the run's per-step panel is the primary view; the raw thread is
      // the whole transcript, so demote it to a clearly-secondary "full conversation" link.
      labelKey:
        event.kind === "workflow_run"
          ? "activities.detail.viewFullConversation"
          : "activities.detail.openThread",
      to: "/$environmentId/$threadId",
      params: { environmentId, threadId: refs.threadId },
    });
  }

  if (refs.agentId && refs.agentId !== "main") {
    links.push({
      id: "agent",
      labelKey: "activities.detail.openAgent",
      to: "/agents/$agentId",
      params: { agentId: refs.agentId },
      ...(refs.agentLabel?.trim() ? { subtitle: refs.agentLabel.trim() } : {}),
    });
  }

  if (refs.workflowId) {
    links.push({
      id: "workflow",
      labelKey: "activities.detail.openWorkflow",
      to: "/workflows/$id",
      params: { id: refs.workflowId },
      ...(refs.workflowName?.trim() ? { subtitle: refs.workflowName.trim() } : {}),
    });
  }

  if (refs.allowanceId && event.status === "needs_approval") {
    links.push({
      id: "allowance",
      labelKey: "activities.detail.openAllowance",
      to: "/allowance",
    });
  }

  if (refs.vaultItemId) {
    links.push({
      id: "file",
      labelKey: "activities.detail.openFile",
      to: "/vault",
      search: { item: refs.vaultItemId },
      ...(refs.fileLabel?.trim() ? { subtitle: refs.fileLabel.trim() } : {}),
    });
  }

  return links;
}
